import './quees.css';
import { reproducir, proximamente } from '../player/player.js';
import { posterDe, videoDe } from '../../config/videos.js';

// Sección — "¿Qué es Zenkai? v2" (Claude Design, proyecto dd25dfb7).
//
// Dos ideas gobiernan esta versión:
//
//   1. LA NEGACIÓN CEDE EL TAMAÑO. El titular se parte en dos: "No son cursos
//      aislados" queda pequeño y gris; "Es una plataforma y comunidad exclusiva
//      para Ingenieros" se lo queda todo. Y el "3×" sale del párrafo para
//      volverse cifra —una cifra se lee, dentro de un párrafo se pierde.
//
//   2. EL VIDEO SE ABRE SOBRE SÍ MISMO, no en un modal. La banda pasa de 32:9 a
//      sangre a 16:9 contenida, y la MISMA transición gobierna ancho, márgenes,
//      radio, proporción y sombra: por eso se lee como una pieza abriéndose y no
//      como un cambio de estado. La página se centra en paralelo, con la misma
//      duración —encuadrar después se sentiría como dos acciones.

// La pieza y su portada se declaran en src/config/videos.js, clave 'quees'.
const POSTER = posterDe('quees');
// La duración NO se escribe aquí: sale de la pieza. Estuvo en "90 s" hasta
// que llegó el corte real, que dura 3:37 — y nadie se entera de que el
// rótulo miente hasta que alguien lo cronometra.
const DURACION = videoDe('quees')?.duracion || '';

const ENSO = `<svg viewBox="0 0 100 100" width="16" height="16" aria-hidden="true"><circle cx="50" cy="52" r="38" fill="none" stroke="currentColor" stroke-width="12" stroke-linecap="round" stroke-dasharray="205 300" transform="rotate(122 50 50)"/></svg>`;

// LOS CUATRO EJES. Son los mismos que narra el recorrido 3D y los mismos que
// nombra el hero: aquí se enuncian, allá se recorren. El 365 lleva su propio
// tono porque no es un cuarto tema, es lo que sostiene a los otros tres.
// El ORDEN es el del método —especialización, negocio, habilidades suaves—,
// que NO es el del recorrido 3D: ahí la escena 03 dibuja habilidades suaves y
// la 04 pensamiento de negocio, y el texto va encima de su escena (ver la
// nota en src/overlay/index.js). Aquí no hay escenario que respetar.
const EJES = [
  { n: '01', t: 'Especialízate', tono: 'uno',
    d: 'Domina las metodologías técnicas más solicitadas en la industria: Lean, Six Sigma, análisis de datos, validación de procesos, ingeniería de calidad, entre otras.' },
  { n: '02', t: 'Pensamiento de negocio',
    d: 'Piensa como empresario. Entiende el lenguaje financiero de la empresa: presupuestos, márgenes, varianza, P&amp;L, etc.' },
  { n: '03', t: 'Habilidades suaves',
    d: 'Desarrolla la capacidad de comunicar de forma efectiva, trabajo en equipo, control emocional, empatía, mentoría, entre otros.' },
  { n: '365', t: 'Constancia y disciplina', tono: 'k',
    d: 'Poner en práctica los 3 ejes anteriores todos los días del año. ¡No es opcional!' },
];

function markup() {
  return `
  <div class="zk-quees__beam" aria-hidden="true"></div>

  <div class="zk-quees__head">
    <div class="zk-quees__eyebrow" data-reveal>${ENSO}<span>¿Qué es Zenkai?</span></div>

    <!-- LA NEGACIÓN CEDE EL TAMAÑO, igual que antes, pero ahora lo que se niega
         es ser un CATÁLOGO —que es cierto y es la diferencia real— en vez del
         título y la cédula. De paso desaparece la repetición con el hero, que
         abre con ese mismo golpe dos secciones más arriba. -->
    <div class="zk-quees__grid">
      <div>
        <h2 class="zk-quees__title" data-reveal>
          <span class="neg">No es un catálogo de cursos.</span>
          <span class="afi">Es un sistema <em>construido</em> para ayudarte a llegar a la Gerencia.</span>
        </h2>
        <p class="zk-quees__desc" data-reveal style="--d:60ms">Una posición gerencial se decide por cuatro ejes. Zenkai te acompaña en los cuatro durante un año, con expertos que los aplican en planta todos los días.</p>
        <p class="zk-quees__desc zk-quees__desc--sm" data-reveal style="--d:120ms">Con <b>asesorías de expertos</b>, <b>sesiones en vivo</b> y una <b>plataforma que se adapta a tu tiempo</b>.</p>
      </div>

      <!-- Los cuatro ejes dejan de ser una lista dentro del párrafo y pasan a ser
           una ficha. Dentro de un texto corrido se leían como enumeración; aquí
           se leen como la ESTRUCTURA del método, que es lo que son. -->
      <div class="zk-quees__metodo" data-reveal style="--d:180ms">
        <div class="zk-quees__metodo-bar">
          <span class="ttl">${ENSO}El Método Zenkai</span>
          <span class="meta">4 ejes · 12 meses</span>
        </div>
        ${EJES.map((e) => `
        <div class="zk-quees__eje">
          <span class="n${e.tono ? ` is-${e.tono}` : ''}">${e.n}</span>
          <div>
            <div class="t">${e.t}</div>
            <div class="d">${e.d}</div>
          </div>
        </div>`).join('')}
      </div>
    </div>
  </div>

  <div class="zk-quees__stage" data-open="false">
    <button type="button" class="zk-quees__frame" aria-label="Reproducir video: Conoce el Método Zenkai, ${DURACION}">
      <img class="zk-quees__poster" alt="" />
      <span class="zk-quees__media"></span>
      <span class="zk-quees__vignette" aria-hidden="true"></span>
      <span class="zk-quees__play" aria-hidden="true"><svg width="26" height="29" viewBox="0 0 26 29"><path d="M0 0l26 14.5L0 29z"/></svg></span>
      <span class="zk-quees__label"><b>Conoce el Método Zenkai</b><span>${DURACION}</span></span>
    </button>
  </div>`;
}

/**
 * Monta la sección "¿Qué es Zenkai?" dentro de `mount`.
 * @returns {HTMLElement} la <section> montada.
 */
export function mountQuees(mount) {
  const section = document.createElement('section');
  section.className = 'zk-quees';
  section.id = 'que-es-zenkai';
  section.setAttribute('aria-label', '¿Qué es Zenkai?');
  section.innerHTML = markup();
  (mount || document.body).appendChild(section);

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stage = section.querySelector('.zk-quees__stage');
  const frame = section.querySelector('.zk-quees__frame');
  const media = section.querySelector('.zk-quees__media');
  const poster = section.querySelector('.zk-quees__poster');

  poster.addEventListener('error', () => frame.classList.add('no-poster'));
  poster.src = POSTER;

  // El destino del scroll se recalcula CADA FRAME: el alto del marco está
  // cambiando (32:9 → 16:9) mientras nos movemos, así que un objetivo fijo
  // dejaría el video descuadrado al terminar.
  const center = () => {
    const aim = () => {
      const r = frame.getBoundingClientRect();
      return window.pageYOffset + r.top - Math.max(0, (window.innerHeight - r.height) / 2);
    };
    if (reduced) { window.scrollTo(0, aim()); return; }
    const lenis = window.__zkLenis;
    if (lenis) lenis.stop();          // si no, reescribe la posición cada frame
    const start = window.pageYOffset;
    const t0 = performance.now();
    const DUR = 560;                  // misma duración que la apertura
    const step = (now) => {
      const p = Math.min(1, (now - t0) / DUR);
      const e = 1 - Math.pow(1 - p, 3);
      window.scrollTo(0, start + (aim() - start) * e);
      if (p < 1) requestAnimationFrame(step);
      else if (lenis) lenis.start();
    };
    requestAnimationFrame(step);
  };

  let open = false;
  frame.addEventListener('click', () => {
    if (open) return;
    open = true;
    stage.dataset.open = 'true';
    // Se abre SIEMPRE: la animación es parte del diseño. Si todavía no hay
    // pieza, el aviso ocupa el marco en lugar de dejarlo en negro.
    if (!reproducir(media, 'quees')) proximamente(media);
    center();
  });

  // Reveal.
  const revealEls = [...section.querySelectorAll('[data-reveal]')];
  if (reduced) { section.classList.add('is-in'); return section; }
  section.classList.add('is-fx');
  revealEls.forEach((el, i) => { el.style.transitionDelay = `${Math.min(i * 90, 480)}ms`; });
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { section.classList.add('is-in'); io.disconnect(); } });
    }, { threshold: 0.12 });
    io.observe(section);
  } else {
    section.classList.add('is-in');
  }

  return section;
}
